import React, { useEffect } from "react";
import { Link, Navigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { ArrowRight, Compass, Layers, FileSignature, Bot, MapPin } from "lucide-react";
import HeaderBar from "../components/HeaderBar";
import Footer from "../components/Footer";
import SEO from "../components/SEO";
import PromptMarketplace from "../components/PromptMarketplace";
import HomeShowcaseCard from "../components/HomeShowcaseCard";
import { trackReferralVisit } from "../lib/api";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
function startGoogleLogin() {
  const redirectUrl = window.location.origin + "/dashboard";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
}

export default function Landing() {
  const { user, loading } = useAuth();
  const [activeReferral, setActiveReferral] = React.useState(null);

  // V2.0.40 — Architect-referral attribution funnel. If the URL carries
  // `?ref=ar_xxxxx` we stash it in sessionStorage so AuthContext can
  // attribute the eventual signup. Also fire a public visit-tracking
  // ping so we can measure click-through independent of conversions.
  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const ref = (params.get("ref") || "").trim();
      const existing = sessionStorage.getItem("blueprint:referral_id");
      const effective = ref && ref.startsWith("ar_") ? ref : existing;
      if (ref && ref.startsWith("ar_")) {
        sessionStorage.setItem("blueprint:referral_id", ref);
        trackReferralVisit({
          referral_id: ref,
          landing_url: window.location.pathname + window.location.search,
          user_agent: navigator.userAgent,
        }).catch(() => {});
      }
      if (effective && effective.startsWith("ar_")) {
        setActiveReferral(effective);
      }
    } catch (err) { console.debug("[Landing] referral attribution failed:", err); }
  }, []);

  if (!loading && user) return <Navigate to="/dashboard" replace />;

  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-50">
      <SEO
        title="Plain-Talk Architectural Design"
        description="2D plans, real-time 3D massing, IFC 4 BIM exports, and AI photo-realistic renders from a single canvas. No CAD background required."
        path="/"
        themeColor="#1c1610"
      />
      <HeaderBar
        rightSlot={
          <button
            onClick={startGoogleLogin}
            data-testid="landing-signin-btn"
            className="font-mono text-[10px] uppercase tracking-widest bg-brass-600 text-zinc-950 px-4 py-2 hover:bg-brass-500 transition-colors"
          >
            Sign in
          </button>
        }
      />

      {/* Hero */}
      <section className="relative overflow-hidden border-b border-zinc-800">
        <div className="absolute inset-0 bp-grid pointer-events-none" />
        <div className="absolute -top-32 -right-24 w-[40rem] h-[40rem] rounded-full bg-brass-500/5 blur-3xl pointer-events-none" />
        <div className="absolute -bottom-32 -left-24 w-[40rem] h-[40rem] rounded-full bg-brass-500/5 blur-3xl pointer-events-none" />

        <div className="relative max-w-7xl mx-auto px-6 lg:px-12 py-20 lg:py-32 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7 space-y-7">
            {activeReferral && (
              <div
                data-testid="landing-referral-banner"
                className="inline-flex items-center gap-2 px-3 py-1.5 border border-brass-500 bg-brass-950/40"
              >
                <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-brass-300">
                  // Referred by an architect ·
                </span>
                <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-brass-200">
                  your first Tier 1 unlock is on us
                </span>
              </div>
            )}
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-brass-400">
              // Architectural Co-pilot · BIM 2026
            </div>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-7xl text-zinc-50 leading-[0.95] tracking-tight">
              Conversation in.<br />
              <span className="text-brass-400">Houses out.</span>
            </h1>
            <p className="font-body text-base sm:text-lg text-zinc-400 max-w-xl leading-relaxed">
              Blueprint AI is a high-fidelity design platform where you talk to an
              architectural assistant and watch a real, BIM-standard 3D home build itself
              in front of you. Walls, windows, materials — every change is geometry, not just chat.
            </p>

            <div className="flex flex-wrap items-center gap-3 pt-2">
              <button
                onClick={startGoogleLogin}
                data-testid="hero-cta-btn"
                className="group inline-flex items-center gap-3 bg-brass-600 hover:bg-brass-500 text-zinc-950 font-mono text-xs uppercase tracking-widest px-6 py-4 transition-colors"
              >
                <span>Start designing free</span>
                <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" />
              </button>
              <a
                href="#how"
                className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-widest text-zinc-300 hover:text-zinc-50 px-6 py-4 border border-zinc-800 hover:border-zinc-600 transition-colors"
              >
                See how it works
              </a>
              {/* V2.0.35 — Local Code Finder promoted from footer to
                  the hero CTA row so it acts as a no-signup lead-magnet
                  funnel right next to the primary actions. */}
              <Link
                to="/local-code-finder"
                data-testid="hero-local-code-finder-btn"
                className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-widest text-brass-300 hover:text-brass-200 px-6 py-4 border border-brass-700/60 hover:border-brass-500 bg-brass-950/30 transition-colors"
              >
                <MapPin className="w-3.5 h-3.5" />
                Local Code Finder
              </Link>
            </div>

            <div className="grid grid-cols-3 max-w-md pt-6 border-t border-zinc-800">
              {[
                ["Models", "BIM 2.0"],
                ["Engine", "Three.js"],
                ["AI", "Gemini 3 Pro"],
              ].map(([k, v]) => (
                <div key={k}>
                  <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">
                    {k}
                  </div>
                  <div className="font-mono text-xs text-zinc-100 mt-1">{v}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="lg:col-span-5 relative">
            <div className="relative aspect-square border border-zinc-800 bg-zinc-900 overflow-hidden">
              <img
                src="https://static.prod-images.emergentagent.com/jobs/fabe4fb9-68b6-4f8f-9776-56cb30ea5cc2/images/d66d6a11be473a9e50b0444c28aaea11fb52d0c10b68baf534b874ba604f9737.png"
                alt="Architectural render"
                className="absolute inset-0 w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-tr from-zinc-950/80 via-transparent to-transparent" />
              <div className="absolute top-4 left-4 font-mono text-[10px] uppercase tracking-widest text-brass-400">
                <div>// 3D Render</div>
                <div className="text-zinc-300 mt-1">villa_modern_v3.bim</div>
              </div>
              <div className="absolute bottom-4 left-4 right-4 grid grid-cols-3 gap-2">
                {[
                  ["Walls", "12"],
                  ["Windows", "8"],
                  ["Sqft", "2,140"],
                ].map(([k, v]) => (
                  <div
                    key={k}
                    className="border border-zinc-700/70 bg-zinc-950/70 backdrop-blur px-2 py-1.5"
                  >
                    <div className="font-mono text-[8px] uppercase tracking-widest text-zinc-400">{k}</div>
                    <div className="font-mono text-xs text-zinc-50 mt-0.5">{v}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section id="how" className="border-b border-zinc-800">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-20 lg:py-28">
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-brass-400 mb-3">
            // The Workflow
          </div>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl text-zinc-50 tracking-tight max-w-3xl">
            From a sentence to a structurally-modeled home in three steps.
          </h2>
          <div className="grid md:grid-cols-3 gap-px bg-zinc-800 mt-12 border border-zinc-800">
            {[
              {
                icon: Bot,
                tag: "01",
                title: "Talk to the architect",
                body:
                  "Describe rooms, materials, vibes. Gemini 3 Pro returns a friendly reply plus a structured BIM payload.",
              },
              {
                icon: Layers,
                tag: "02",
                title: "Watch geometry assemble",
                body:
                  "The right pane is a real Three.js scene. Walls extrude, windows cut out, doors carve through — instantly.",
              },
              {
                icon: FileSignature,
                tag: "03",
                title: "Send to any architect",
                body:
                  "Export to IFC — the open BIM standard. Opens natively in Revit, ArchiCAD, Vectorworks, BricsCAD, and every other major BIM tool. Plus a multi-page PDF for review + signoff.",
              },
            ].map(({ icon: Icon, tag, title, body }) => (
              <div key={tag} className="bg-zinc-950 p-7">
                <div className="flex items-center justify-between mb-6">
                  <Icon className="w-5 h-5 text-brass-400" />
                  <span className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">
                    {tag}
                  </span>
                </div>
                <h3 className="font-display text-xl text-zinc-50 tracking-tight">{title}</h3>
                <p className="font-body text-sm text-zinc-400 mt-3 leading-relaxed">{body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Footer CTA */}
      <PromptMarketplace />

      {/* V2.0.89 — Homepage showcase teaser (top-3 transformations).
          Silently hides itself when there are no discoverable items. */}
      <HomeShowcaseCard />

      <section className="max-w-7xl mx-auto px-6 lg:px-12 py-20 lg:py-28">
        <div className="grid lg:grid-cols-2 gap-10 items-center">
          <div>
            <Compass className="w-8 h-8 text-brass-400 mb-4" />
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl text-zinc-50 tracking-tight">
              Your studio, in a chat box.
            </h2>
            <p className="font-body text-base text-zinc-400 mt-4 max-w-lg leading-relaxed">
              Sign in with Google. Every project autosaves. Every refresh, your house is
              exactly where you left it.
            </p>
            <button
              onClick={startGoogleLogin}
              data-testid="footer-cta-btn"
              className="mt-6 inline-flex items-center gap-3 bg-brass-600 hover:bg-brass-500 text-zinc-950 font-mono text-xs uppercase tracking-widest px-6 py-4 transition-colors"
            >
              Continue with Google <ArrowRight className="w-4 h-4" />
            </button>
          </div>
          <div className="border border-zinc-800 aspect-video relative overflow-hidden bg-zinc-900">
            <img
              src="https://static.prod-images.emergentagent.com/jobs/fabe4fb9-68b6-4f8f-9776-56cb30ea5cc2/images/a3ac9a29c28f3807ceb7edaae833d512bcbdd26c6991be3ceb5797487dda4ff7.png"
              alt="Wireframe to render"
              className="absolute inset-0 w-full h-full object-cover opacity-90"
            />
          </div>
        </div>
      </section>

      <footer className="border-t border-zinc-800 py-8">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 flex flex-col md:flex-row items-start md:items-center justify-between gap-3">
          <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">
            © 2026 Blueprint AI · v1.0
          </div>
          <div className="flex items-center gap-5 font-mono text-[10px] uppercase tracking-widest text-zinc-500">
            <Link
              to="/local-code-finder"
              data-testid="landing-footer-code-finder-link"
              className="hover:text-brass-400 transition-colors"
            >
              // Local Code Finder
            </Link>
            <Link
              to="/terms"
              data-testid="landing-footer-terms-link"
              className="hover:text-brass-400 transition-colors"
            >
              // Terms of Service
            </Link>
            <span className="hidden md:inline">
              Built for architects, by an AI that loves cantilevers.
            </span>
          </div>
        </div>
      </footer>
      {/* V2.0.50 — Canonical public footer (copyright + Terms + Privacy + support email). */}
      <Footer />
    </div>
  );
}
